/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { SelectionFieldRefs } from '@airasia-phoenix/react-native/components/SelectionFieldView';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Keyboard } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useLogger } from '@airasia-rn/core-logger';
import { useOpenUrl } from '../../hooks/useOpenUrl';
import { useSignupFeatureFlags } from '../../hooks/useSignupFeatureFlags';
import {
  type SignUpFormNotification,
  useSignUpFormSubmit,
} from '../../hooks/useSignUpFormSubmit';
import { useUser } from '../../hooks/account';
import { ACTIONS, CATEGORY, SCREEN_NAME } from '../../config/analytics';
import { DateUtils } from '../../utils/dateUtils';
import type { SelectedCountry } from '../../utils/mapCountries';
import { withPlusDialingCode } from '../../utils/phoneUtils';
import type { SignUpFormFieldErrors } from '../../utils/signUpFormUtils';
import {
  createAnalyticsLogger,
  getSsoSignupModalLabels,
  logSsoAuthSuccess,
  resolveSsoEmailMethod,
} from '../../utils/loginSignupAnalyticsUtils';
import {
  validateDialingCode,
  validateMobileNumber,
} from '../../utils/signupLoginUtils';
import {
  buildParentalConsentLabel,
  GENERAL_TERM_URLS,
  getCountryIndexValues,
  getFilledFieldEndIcon,
  getGroupedCountryData,
  getRequiredFieldError,
} from './signUpFormViewUtils';
import {
  defaultProps,
  Gender,
  getGenderOptions,
  MobileLoginType,
  SignUpFormProps,
} from './types';

/**
 * Form state, validation handlers, and submit wiring for SignUpFormView.
 * Keeps the view file focused on layout/JSX.
 */
export function useSignUpFormView(props: SignUpFormProps) {
  const { t } = useTranslation('account');
  const openUrl = useOpenUrl();
  const { logEvent } = useLogger();
  const { parental_guidance: isParentalConsentEnabled } =
    useSignupFeatureFlags();

  const {
    headings,
    genderLabelProps,
    genderErrorProps,
    givenNameProps,
    surNameProps,
    primaryButtonProps,
    mobileFieldProps,
    isDialingCodeDisabled,
    dobProps,
    onCloseIconClick,
    email,
    password,
    onSignUpSuccess,
    onSignUpError,
    onLoadingChange,
    isIncompleteSignup,
    onNavigateToVerifyOtp,
    signupMethod,
  } = { ...defaultProps, ...props };

  const { data: user } = useUser('SignUpFormView');

  // Use the sign-up method if passed, otherwise the email method.
  const analyticsMethod =
    signupMethod ?? resolveSsoEmailMethod(Boolean(isIncompleteSignup));
  const userDetailsLabels = useMemo(
    () => getSsoSignupModalLabels(analyticsMethod),
    [analyticsMethod],
  );
  const logUserDetailsAnalytics = useMemo(
    () =>
      createAnalyticsLogger(
        logEvent,
        SCREEN_NAME.userDetailsV2,
        CATEGORY.ssoSignup,
      ),
    [logEvent],
  );

  useEffect(() => {
    logUserDetailsAnalytics(ACTIONS.screenView, userDetailsLabels.userDetails);
  }, [logUserDetailsAnalytics, userDetailsLabels.userDetails]);

  const handleSignUpSuccess = useCallback(() => {
    logSsoAuthSuccess(logEvent, true, analyticsMethod, user?.id);
    onSignUpSuccess?.();
  }, [analyticsMethod, logEvent, onSignUpSuccess, user?.id]);

  const [mobileLogin, setMobileLogin] = useState<MobileLoginType>({
    mobileNumber: '',
    dialingCode: '+60',
  });
  const [mobileErrorMessage, setMobileErrorMessage] = useState('');
  const [dialingCodeErrorMessage, setDialingCodeErrorMessage] = useState('');
  const mobileFieldRef = useRef<SelectionFieldRefs>(null);
  const [countrySearch, setCountrySearch] = useState('');

  const [selectedGender, setSelectedGender] = useState('');
  const [notification, setNotification] =
    useState<SignUpFormNotification | null>(null);
  const [givenName, setGivenName] = useState('');
  const [surName, setSurName] = useState('');
  const [dob, setDob] = useState('');
  const [hasParentalConsent, setHasParentalConsent] = useState(false);
  const [parentalConsentLabelWidth, setParentalConsentLabelWidth] = useState(0);
  const [givenNameErrorMessage, setGivenNameErrorMessage] = useState('');
  const [surNameErrorMessage, setSurNameErrorMessage] = useState('');
  const [dobError, setDobError] = useState('');
  const [genderError, setGenderError] = useState('');
  const [isGivenNameFocused, setIsGivenNameFocused] = useState(false);
  const [isSurNameFocused, setIsSurNameFocused] = useState(false);

  const applyFieldErrors = useCallback((errors: SignUpFormFieldErrors) => {
    setGenderError(errors.genderError);
    setGivenNameErrorMessage(errors.givenNameError);
    setSurNameErrorMessage(errors.surNameError);
    setDobError(errors.dobError);
    setDialingCodeErrorMessage(errors.dialingCodeError);
    setMobileErrorMessage(errors.mobileError);
  }, []);

  const { handleSubmit, isApiLoading } = useSignUpFormSubmit({
    t,
    email,
    password,
    isIncompleteSignup,
    onSignUpSuccess: handleSignUpSuccess,
    onSignUpError,
    onNavigateToVerifyOtp,
    setNotification,
    applyFieldErrors,
  });

  useEffect(() => {
    onLoadingChange?.(isApiLoading);
  }, [isApiLoading, onLoadingChange]);

  const handleDialingCodeChange = useCallback(
    (selectedCountryCode: string) => {
      const formattedCode = withPlusDialingCode(selectedCountryCode);
      setMobileLogin((prev) => ({ ...prev, dialingCode: formattedCode }));
      setDialingCodeErrorMessage(validateDialingCode(formattedCode, t));
    },
    [t],
  );

  const handleMobileNumberChange = useCallback(
    (value: string) => {
      setMobileLogin((prev) => ({ ...prev, mobileNumber: value }));
      setMobileErrorMessage(validateMobileNumber(value, t));
    },
    [t],
  );

  const showParentalConsent =
    isParentalConsentEnabled && DateUtils.isMinorAge(dob);

  useEffect(() => {
    if (!showParentalConsent) {
      setHasParentalConsent(false);
    }
  }, [showParentalConsent]);

  const parentalConsentLabel = useMemo(
    () =>
      buildParentalConsentLabel(t, () =>
        openUrl(GENERAL_TERM_URLS.PRIVACY_STATEMENT),
      ),
    [openUrl, t],
  );

  const isContinueDisabled = showParentalConsent && !hasParentalConsent;

  const handleGivenNameChange = useCallback(
    (value: string) => {
      setGivenName(value);
      setGivenNameErrorMessage(getRequiredFieldError(value, t));
    },
    [t],
  );

  const handleSurNameChange = useCallback(
    (value: string) => {
      setSurName(value);
      setSurNameErrorMessage(getRequiredFieldError(value, t));
    },
    [t],
  );

  const handleDobChange = useCallback((value: string) => {
    setDob(value);
    // The web date field emits '' on mount and for a partly typed date, so an
    // empty value is not an error here; submit validation covers a missing DOB.
    if (value) setDobError('');
  }, []);

  const handleDobPickerSheetStateChange = useCallback((isVisible: boolean) => {
    if (isVisible) {
      Keyboard.dismiss();
    }
  }, []);

  const handleGenderChange = useCallback(
    (selectedValues: { value: string }[]) => {
      if (selectedValues.length > 0) {
        setSelectedGender(selectedValues[0].value as Gender);
        setGenderError('');
      }
    },
    [],
  );

  const handleCountrySelect = useCallback(
    (selected: SelectedCountry[] = []) => {
      const selectedItem = selected[0];
      if (selectedItem?.model?.dialingCode) {
        handleDialingCodeChange(selectedItem.model.dialingCode);
      }
      mobileFieldRef.current?.toggleBottomSheetView();
      setCountrySearch('');
    },
    [handleDialingCodeChange],
  );

  const toggleParentalConsent = useCallback(() => {
    setHasParentalConsent((prev) => !prev);
  }, []);

  const onParentalConsentLabelLayout = useCallback((nextWidth: number) => {
    if (nextWidth <= 0) return;
    setParentalConsentLabelWidth((prevWidth) =>
      nextWidth !== prevWidth ? nextWidth : prevWidth,
    );
  }, []);

  const genderOptions = useMemo(
    () =>
      getGenderOptions(t).map((option) => ({
        ...option,
        isSelected: option.value === selectedGender,
      })),
    [t, selectedGender],
  );

  const filteredCountryData = useMemo(
    () => getGroupedCountryData(countrySearch),
    [countrySearch],
  );

  const indexValues = useMemo(
    () => getCountryIndexValues(filteredCountryData),
    [filteredCountryData],
  );

  const onContinuePress = useCallback(() => {
    logUserDetailsAnalytics(ACTIONS.tap, userDetailsLabels.submitUserDetails);
    handleSubmit({
      selectedGender,
      givenName,
      surName,
      dob,
      dialingCode: mobileLogin.dialingCode,
      mobileNumber: mobileLogin.mobileNumber,
      requireParentalConsent: showParentalConsent,
      hasParentalConsent,
    });
  }, [
    dob,
    givenName,
    handleSubmit,
    hasParentalConsent,
    logUserDetailsAnalytics,
    mobileLogin.dialingCode,
    mobileLogin.mobileNumber,
    selectedGender,
    showParentalConsent,
    surName,
    userDetailsLabels.submitUserDetails,
  ]);

  return {
    t,
    // resolved presentational props
    headings,
    genderLabelProps,
    genderErrorProps,
    givenNameProps,
    surNameProps,
    primaryButtonProps,
    mobileFieldProps,
    isDialingCodeDisabled,
    dobProps,
    onCloseIconClick,
    // form values
    givenName,
    surName,
    dob,
    mobileLogin,
    countrySearch,
    setCountrySearch,
    hasParentalConsent,
    // errors / focus
    notification,
    givenNameErrorMessage,
    surNameErrorMessage,
    dobError,
    genderError,
    mobileErrorMessage,
    dialingCodeErrorMessage,
    isGivenNameFocused,
    setIsGivenNameFocused,
    isSurNameFocused,
    setIsSurNameFocused,
    // derived
    genderOptions,
    filteredCountryData,
    indexValues,
    showParentalConsent,
    parentalConsentLabel,
    parentalConsentLabelWidth,
    isContinueDisabled,
    isApiLoading,
    mobileFieldRef,
    // handlers
    getFilledFieldEndIcon,
    handleGivenNameChange,
    handleSurNameChange,
    handleDobChange,
    handleDobPickerSheetStateChange,
    handleGenderChange,
    handleMobileNumberChange,
    handleDialingCodeChange,
    handleCountrySelect,
    toggleParentalConsent,
    onParentalConsentLabelLayout,
    onContinuePress,
  };
}
